/**
 * web/src/components/shared/Skeleton.tsx
 * Loading placeholder primitive. Variants: row (full-width line),
 * text (3/4-width line), card (full-width block matching RowCard
 * dimensions), circle (matches a Dot or avatar).
 *
 * Per CLAUDE.md: skeleton loaders for page content, inline spinners for
 * action buttons. Animation uses Tailwind's built-in `animate-pulse` and
 * `motion-reduce:animate-none` honors prefers-reduced-motion (CONTEXT.md
 * D-3.3 — Tailwind motion modifiers handle reduced motion).
 */
import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/** Public props for Skeleton. */
export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'row' | 'card' | 'circle' | 'text';
  width?: number | string;
  height?: number | string;
}

/** Variant → Tailwind utility map. */
const variantCls: Record<NonNullable<SkeletonProps['variant']>, string> = {
  row: 'h-3 w-full rounded',
  text: 'h-4 w-3/4 rounded',
  card: 'h-[68px] w-full rounded-lg',
  circle: 'rounded-full',
};

/**
 * Renders a single skeleton placeholder.
 * Side effects: none — `aria-hidden="true"` so screen readers skip it.
 *
 * @param variant 'row' | 'text' | 'card' | 'circle' (default: 'row')
 * @param width override width (number → px, string → CSS)
 * @param height override height (number → px, string → CSS)
 */
export function Skeleton({
  variant = 'row',
  width,
  height,
  className,
  style,
  ...props
}: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'animate-pulse motion-reduce:animate-none bg-bg-muted',
        variantCls[variant],
        className,
      )}
      style={{
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
        ...style,
      }}
      {...props}
    />
  );
}

/**
 * Convenience for rendering N stacked skeleton placeholders. Useful as a
 * page-level fallback for ApprovalQueue / Confirmations / Workflows lists
 * while the first query resolves.
 *
 * @param count number of placeholders to render (default: 5)
 * @param variant which Skeleton variant to stack (default: 'card')
 * @param label optional context line above the stack saying WHAT is loading
 *   ("Loading speakers from the monday board…") — bare grey bars read as
 *   "stuck" when the fetch is slow. `role="status"` announces it politely
 *   to screen readers (the bars themselves are aria-hidden).
 */
export function SkeletonList({
  count = 5,
  variant = 'card' as SkeletonProps['variant'],
  label,
}: {
  count?: number;
  variant?: SkeletonProps['variant'];
  label?: string;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {label && (
        <div role="status" className="text-xs text-text-subtle">
          {label}
        </div>
      )}
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} variant={variant} />
      ))}
    </div>
  );
}
